import { useMemo, useRef } from "react";
import { Canvas } from "@react-three/fiber";
import { Stars } from "@react-three/drei";
import {
  Constellation,
  ClusterNebula,
  ClusterLabel,
} from "@chuzi/shared/realms/cosmos/components";
import { t } from "@chuzi/shared/config";
import type {
  SceneListItem,
  SceneMapEntry,
  StoryListItem,
  RealmId,
  TreeGraph,
} from "@chuzi/shared/types";
import { isSystemStory } from "@chuzi/shared/types";
import { DirectorAvatar } from "./director-avatar";
import { StarFocusGlow } from "./star-focus-glow";
import { PinnedCosmosWorldMover } from "./pinned-cosmos-world-mover";
import {
  buildCatalogConstellationGroups,
  type CatalogConstellationGroup,
} from "../lib/catalog-constellation-data";
import {
  computeTagOverviewFrame,
  computeClusterOverviewFrame,
  filterTagClusters,
  tagNebulaColor,
  type TagNebulaCluster,
  type TagNebulaLayout,
} from "../lib/tag-nebula-layout";
import { CHUZI_NEBULA_COLOR } from "../lib/chuzi-nebula-layout";
import {
  cosmosBrowseAppearance,
  cosmosBrowseClusterLabelHidden,
  cosmosBrowseShowNebulaStoryLabels,
} from "../lib/cosmos-browse";
import {
  pinnedCameraForAllLevel,
  vec3FromTuple,
} from "../lib/cosmos-world-mover";
import * as THREE from "three";

export type AllPlacedStory = {
  story: StoryListItem;
  anchor: [number, number, number];
};

type AllNavLevel = "nebula" | "story" | "scene";

interface AllCosmosSceneProps {
  layout: TagNebulaLayout;
  chuziCluster: TagNebulaCluster;
  chuziNebulaLabel: string;
  visibleStories: StoryListItem[];
  tagQuery: string;
  /** Changes when tag filter or sort changes — triggers a brief overview fly-in. */
  viewResetKey: string;
  focusedIndex: number;
  focusedStoryId?: string | null;
  sceneIndex?: number;
  realm: RealmId;
  creatorAvatars: Record<string, string>;
  coverboxes: Record<string, string | null>;
  scenes: Record<string, SceneListItem[]>;
  sceneMaps: Record<string, Record<string, SceneMapEntry>>;
  storyGraphs: Record<string, TreeGraph>;
  onHudProject: (pos: { x: number; y: number } | null) => void;
  onSelect: (index: number) => void;
  navLevel?: AllNavLevel;
  focusedClusterCenter?: [number, number, number] | null;
  focusedClusterRadius?: number;
}

export function buildAllPlacedStories(
  stories: StoryListItem[],
  layout: TagNebulaLayout,
): AllPlacedStory[] {
  return stories.map((story) => ({
    story,
    anchor: layout.storyPositions.get(story.id) ?? [0, 0, 0],
  }));
}

function resolveAllFocusTarget(input: {
  navLevel: AllNavLevel;
  placed: AllPlacedStory[];
  focusedIndex: number;
  sceneIndex: number;
  constellationGroups: CatalogConstellationGroup[];
  focusedClusterCenter?: [number, number, number] | null;
  overviewLookAt: [number, number, number];
}): THREE.Vector3 {
  const {
    navLevel,
    placed,
    focusedIndex,
    sceneIndex,
    constellationGroups,
    focusedClusterCenter,
    overviewLookAt,
  } = input;

  if (navLevel === "nebula" && focusedClusterCenter) {
    return vec3FromTuple(focusedClusterCenter);
  }

  if (navLevel === "scene") {
    const group = constellationGroups.find((entry) => entry.storyIndex === focusedIndex);
    const scene = group?.scenes[sceneIndex];
    if (scene?.visual.position) {
      return vec3FromTuple(scene.visual.position);
    }
  }

  if (placed.length > 0 && focusedIndex < placed.length) {
    return vec3FromTuple(placed[focusedIndex].anchor);
  }

  return vec3FromTuple(overviewLookAt);
}

export function AllCosmosScene({
  layout,
  chuziCluster,
  chuziNebulaLabel,
  visibleStories,
  tagQuery,
  viewResetKey,
  focusedIndex,
  focusedStoryId = null,
  sceneIndex = 0,
  realm,
  creatorAvatars,
  coverboxes,
  scenes,
  sceneMaps,
  storyGraphs,
  onHudProject,
  onSelect,
  navLevel = "nebula",
  focusedClusterCenter,
  focusedClusterRadius,
}: AllCosmosSceneProps) {
  const worldGroupRef = useRef<THREE.Group>(null);

  const placed = useMemo(
    () => buildAllPlacedStories(visibleStories, layout),
    [visibleStories, layout],
  );

  const constellationGroups = useMemo(
    () =>
      buildCatalogConstellationGroups({
        stories: visibleStories,
        storyPositions: layout.storyPositions,
        scenes,
        storyGraphs,
        sceneMaps,
        coverboxes,
        focusedIndex,
        includePreviews: false,
      }),
    [
      visibleStories,
      layout.storyPositions,
      scenes,
      storyGraphs,
      sceneMaps,
      coverboxes,
      focusedIndex,
    ],
  );

  const visibleClusters = useMemo(
    () => filterTagClusters(layout.clusters, tagQuery),
    [layout.clusters, tagQuery],
  );
  const visibleStoryIds = useMemo(
    () => new Set(visibleStories.map((story) => story.id)),
    [visibleStories],
  );
  const overviewFrame = useMemo(
    () =>
      computeTagOverviewFrame(
        visibleClusters,
        layout.storyPositions,
        visibleStoryIds,
        chuziCluster,
      ),
    [visibleClusters, layout.storyPositions, visibleStoryIds, chuziCluster],
  );
  const clusterOverviewFrame = useMemo(() => {
    if (focusedClusterCenter && focusedClusterRadius != null) {
      return computeClusterOverviewFrame({
        tag: "",
        center: focusedClusterCenter,
        radius: focusedClusterRadius,
        storyIds: [],
      });
    }
    return computeClusterOverviewFrame(chuziCluster);
  }, [focusedClusterCenter, focusedClusterRadius, chuziCluster]);

  const activeFocusedStoryId =
    focusedStoryId ??
    (placed.length > 0 && focusedIndex < placed.length
      ? placed[focusedIndex].story.id
      : null);

  const catalogAppearance = useMemo(
    () => cosmosBrowseAppearance(navLevel),
    [navLevel],
  );

  const showNebulaStoryLabels = cosmosBrowseShowNebulaStoryLabels(navLevel);
  const hideClusterLabels = cosmosBrowseClusterLabelHidden(navLevel);

  const focusTarget = useMemo(
    () =>
      resolveAllFocusTarget({
        navLevel,
        placed,
        focusedIndex,
        sceneIndex,
        constellationGroups,
        focusedClusterCenter,
        overviewLookAt: overviewFrame.goalLookAt,
      }),
    [
      navLevel,
      placed,
      focusedIndex,
      sceneIndex,
      constellationGroups,
      focusedClusterCenter,
      overviewFrame.goalLookAt,
    ],
  );

  const focusKey = `${navLevel}|${focusedIndex}|${sceneIndex}|${focusTarget.x},${focusTarget.y},${focusTarget.z}`;

  const hudWorldPosition = useMemo(() => {
    if (navLevel === "nebula" || placed.length === 0 || focusedIndex >= placed.length) {
      return null;
    }
    return focusTarget.clone();
  }, [navLevel, placed.length, focusedIndex, focusTarget]);

  const focusedNebulaIndex = useMemo(() => {
    if (navLevel !== "nebula" || !focusedClusterCenter) return -1;
    const clusters = [chuziCluster, ...visibleClusters];
    return clusters.findIndex(
      (cluster) =>
        cluster.center[0] === focusedClusterCenter[0] &&
        cluster.center[1] === focusedClusterCenter[1] &&
        cluster.center[2] === focusedClusterCenter[2],
    );
  }, [navLevel, focusedClusterCenter, chuziCluster, visibleClusters]);

  return (
    <Canvas
      camera={{ position: [0, 18, 42], fov: 60, near: 0.1, far: 500 }}
      dpr={[1, 2]}
      gl={{ antialias: true }}
      style={{ width: "100%", height: "100%" }}
    >
      <color attach="background" args={["#04070d"]} />
      <ambientLight intensity={0.15} />
      <Stars radius={120} depth={60} count={3000} factor={4} fade saturation={0.4} />

      <group ref={worldGroupRef}>
        <group key="chuzi-nebula">
          <ClusterNebula
            center={chuziCluster.center}
            radius={chuziCluster.radius}
            color={CHUZI_NEBULA_COLOR}
            opacity={0.14}
            particleCount={220}
            highlighted={focusedNebulaIndex === 0}
          />
          <ClusterLabel
            position={chuziCluster.center}
            label={chuziNebulaLabel}
            color={CHUZI_NEBULA_COLOR}
            highlighted={focusedNebulaIndex === 0}
            offsetY={chuziCluster.radius * 0.72}
            hidden={hideClusterLabels}
          />
        </group>

        {visibleClusters.map((cluster, clusterIdx) => (
          <group key={`tag-nebula-${cluster.tag}`}>
            <ClusterNebula
              center={cluster.center}
              radius={cluster.radius}
              color={tagNebulaColor(cluster.tag)}
              opacity={tagQuery.trim() ? 0.13 : 0.09}
              particleCount={180}
              highlighted={focusedNebulaIndex === clusterIdx + 1}
            />
            <ClusterLabel
              position={cluster.center}
              label={`#${cluster.tag}`}
              color={tagNebulaColor(cluster.tag)}
              highlighted={focusedNebulaIndex === clusterIdx + 1}
              offsetY={cluster.radius * 0.72}
              hidden={hideClusterLabels}
            />
          </group>
        ))}

        {constellationGroups.map((group) => {
          const isStoryFocused = group.story.id === activeFocusedStoryId;
          const directorId = group.story.creator?.id ?? null;
          const directorAvatarUrl = directorId ? creatorAvatars[directorId] ?? null : null;
          const systemStory = isSystemStory(group.story);
          const activeSceneId = group.scenes[sceneIndex]?.id;

          return (
            <Constellation
              key={`all-constellation-${group.story.id}`}
              published
              storyFocused={isStoryFocused && navLevel === "story"}
              showStoryTitle={isStoryFocused && navLevel === "story"}
              storyTitle={group.story.title}
              storyOverlay={
                isStoryFocused && navLevel === "story"
                  ? systemStory
                    ? {
                        overlayMode: "system",
                        publishedVersion: group.story.published_version ?? null,
                        publishedVersionLabel: t(realm, "published_version_label"),
                        editable: false,
                      }
                    : {
                        chosenByPrefix: t("cosmos", "chosen_by"),
                        directorName: group.story.creator?.name ?? null,
                        directorAvatarUrl,
                        directorAvatarFallback: directorId ? (
                          <DirectorAvatar seed={directorId} realm="cosmos" size={20} />
                        ) : undefined,
                        contentRating: group.story.content_rating,
                        genre: group.story.genre,
                        editable: false,
                      }
                  : undefined
              }
              scenes={group.scenes.map((scene) => ({
                ...scene,
                previewContent: undefined,
                previewImageUrl: undefined,
                label: scene.isTitle ? group.story.title : (scene.label ?? ""),
                labelOnly:
                  (showNebulaStoryLabels && scene.isTitle) ||
                  (navLevel === "scene" && !scene.isTitle),
                focused:
                  isStoryFocused &&
                  (scene.isTitle ||
                    (navLevel === "scene" && scene.id === activeSceneId)),
                controlsSlot: undefined,
              }))}
              edges={group.edges}
              appearance={catalogAppearance}
              showOverlays
              onSceneSelect={(sceneIdx) => {
                if (group.scenes[sceneIdx]?.isTitle) {
                  onSelect(group.storyIndex);
                }
              }}
            />
          );
        })}

        {activeFocusedStoryId ? (
          (() => {
            const focusedPlaced = placed.find((entry) => entry.story.id === activeFocusedStoryId);
            return focusedPlaced ? (
              <StarFocusGlow
                position={focusedPlaced.anchor}
                hue={210}
                intensity={1}
              />
            ) : null;
          })()
        ) : null}
      </group>

      <PinnedCosmosWorldMover
        groupRef={worldGroupRef}
        focusTarget={focusTarget}
        focusKey={focusKey}
        navLevel={navLevel}
        clusterRadius={focusedClusterRadius}
        overviewFrame={overviewFrame}
        clusterOverviewFrame={clusterOverviewFrame}
        viewResetKey={viewResetKey}
        pinnedCamera={pinnedCameraForAllLevel}
        onHudProject={onHudProject}
        hudWorldPosition={hudWorldPosition}
        initialCameraPos={[0, 18, 42]}
      />
    </Canvas>
  );
}
